// What the person sends to an agent, signed by the app around the screen
// (`MeExtensions.instructions`): the desktop app with this PC's key, the
// phone app with the phone's. A screen without it (a browser on Cloud) has
// no post box: it only reads (chat-screen.tsx).
//
// Promise:
// - Each write is signed over what it carries (`@trameria/shared-agent/
//   instruction`'s content per kind) for the device that runs the chat; the
//   store keeps the signature with the row and hands both to the agent.
// - A write the app refused to sign (no list on this device yet) is not sent
//   unsigned: the error says why.
import type { QueryClient } from "@tanstack/react-query";
import type {
  AgentChatDTO,
  AgentEntryDTO,
} from "@trameria/core/agent-chat-shared";
import type {
  ConditionsContent,
  InstructionDraft,
  InstructionKind,
  Signed,
} from "@trameria/shared-agent/instruction";
import { useMeApp } from "../app/context";
import type { Api } from "../data/api";

export interface InstructionSigner {
  /** Signs the drafts at once (one act of the person); throws when it cannot. */
  sign(drafts: InstructionDraft[]): Promise<Signed[]>;
  /** The member this screen signs as (null: its device is in no list yet). */
  member(): Promise<string | null>;
  /**
   * This device's name as its list shows it, for a device that runs no agent
   * of its own (a phone): the screen names it when it says why it cannot
   * reach a device.
   */
  name?(): Promise<string | null>;
  /**
   * Whether other devices' agents may take what this screen signs: false on
   * a PC whose key its other processes could use (it signs for its own agent
   * only), null when it is in no list.
   */
  forOthers?(): Promise<boolean | null>;
}

export class SignRefused extends Error {
  constructor(readonly code: string) {
    super(`the app did not sign (${code})`);
    this.name = "SignRefused";
  }
}

export function useSigner(): InstructionSigner | null {
  return useMeApp().extensions.instructions ?? null;
}

/** The chat's conditions whole, with a change on top (null: its CLI is not known yet). */
export function conditionsOf(
  chat: Pick<AgentChatDTO, "deviceId" | "run">,
  patch: Partial<{
    deviceId: string | null;
    agentKind: string | null;
    model: string | null;
    effort: string | null;
    cwd: string | null;
    otherMcp: AgentChatDTO["run"]["otherMcp"];
  }> = {},
): ConditionsContent | null {
  const device = patch.deviceId !== undefined ? patch.deviceId : chat.deviceId;
  const agentKind =
    patch.agentKind !== undefined ? patch.agentKind : chat.run.agentKind;
  if (!device || (agentKind !== "claude" && agentKind !== "codex")) return null;
  return {
    device,
    agentKind,
    model: patch.model !== undefined ? patch.model : chat.run.model,
    effort: patch.effort !== undefined ? patch.effort : chat.run.effort,
    cwd: patch.cwd !== undefined ? patch.cwd : chat.run.cwd,
    otherMcp: patch.otherMcp !== undefined ? patch.otherMcp : chat.run.otherMcp,
  };
}

/** The hashes of a post's files, in order (null: one is not known, so it cannot be signed). */
export function fileHashes(
  files: readonly Pick<AgentEntryDTO["files"][number], "hash">[],
): string[] | null {
  const out: string[] = [];
  for (const f of files) {
    if (!f.hash) return null;
    out.push(f.hash);
  }
  return out;
}

/** The chat as the screen holds it, else as read now (its device is what is signed for). */
export async function chatForSigning(
  qc: QueryClient,
  api: Api,
  chatId: string,
): Promise<AgentChatDTO> {
  for (const [, c] of qc.getQueriesData<AgentChatDTO>({
    queryKey: ["me", "agents", "chat"],
  }))
    if (c?.id === chatId || c?.key === chatId) return c;
  return api<AgentChatDTO>(
    `/api/me/agents/chats/${encodeURIComponent(chatId)}`,
  );
}

/** Signs one write (null: this screen has no signer, so it goes unsigned). */
export async function signOne(
  signer: InstructionSigner | null,
  draft: {
    device: string | null;
    chat: string | null;
    kind: InstructionKind;
    content: unknown;
  },
): Promise<Signed | undefined> {
  if (!signer) return undefined;
  if (!draft.device) throw new SignRefused("no_device");
  const [signed] = await signer.sign([{ ...draft, device: draft.device }]);
  if (!signed) throw new SignRefused("empty");
  return signed;
}
